import { Divider } from 'component-library';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IndicatorName } from '@/api/rest/resources/types/fact';
import { GraphFact } from '@/components/Charts/types';
import { IndicatorProvider } from '@/components/IndicatorCard/IndicatorContextProvider';
import { NCCard, NCCard2SectionLayout, NCCardComplianceFooter, NCCardHeader } from '@/components/NCCard/NCCard';
import { PrecipitationExplainer } from '@/components/NCExplainers/PrecipitationExplainer';
import { COMPLIANCE_LOGOS_WATER } from '@/config/constants';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray } from '@/pages/shared/hooks/utils';
import { isNullish } from '@/utils/booleans';

import { PlotCardWrapper } from '../PlotCardWrapper';
import { PrecipitationStatsTile } from './HistoricalStatsTile';
import { SMPrecipitationGraphTile } from './SMPrecipitationGraphTile';

export const Precipitation = () => {
  const { t } = useTranslation();

  const [showCard, setShowCard] = useState(true);

  if (!showCard) {
    return null;
  }

  return (
    <PlotCardWrapper name={t('shared.ncaDetail.details.water.labels.precipitation')}>
      <IndicatorProvider>
        <PrecipitationPlotCard setShowCard={setShowCard} />
      </IndicatorProvider>
    </PlotCardWrapper>
  );
};

const PrecipitationPlotCard = ({ setShowCard }: { setShowCard: (show: boolean) => void }) => {
  const { t } = useTranslation();

  const factSet = {
    graph: IndicatorName.water_and_soil_moisture_historic_total_graph,
    latestAnalysis: IndicatorName.soil_moisture_smooth_timeseries_total,
    smTrend: IndicatorName.soil_moisture_trend_change,
    precipitationTrend: IndicatorName.precipitation_trend_change,
  };

  const facts = usePlotFacts(Object.values(factSet));

  const graphData = getFactFromFactArray<GraphFact[]>(facts, factSet.graph)?.value;

  const latestAnalysisFact = getFactFromFactArray<number>(facts, factSet.latestAnalysis);
  const smTrendFact = getFactFromFactArray<number>(facts, factSet.smTrend);
  const precipitationTrendFact = getFactFromFactArray<number>(facts, factSet.precipitationTrend);

  const hasChartData = !isNullish(graphData);
  const hasStatsData =
    !isNullish(latestAnalysisFact?.value) ||
    !isNullish(smTrendFact?.value) ||
    !isNullish(precipitationTrendFact?.value);

  useEffect(() => {
    if (!hasChartData && !hasStatsData) {
      setShowCard(false);
    }
  }, [hasChartData, hasStatsData, setShowCard]);

  return (
    <NCCard explainer={<PrecipitationExplainer />} data-testid='precipitation-nca'>
      <NCCardHeader
        title={t('shared.ncaDetail.details.water.labels.precipitation')}
        className='lg:grid-cols-[1fr_1fr]'
      />
      <NCCard2SectionLayout>
        <SMPrecipitationGraphTile graphData={graphData} />
        <PrecipitationStatsTile
          latestAnalysisFact={latestAnalysisFact}
          smTrendFact={smTrendFact}
          precipitationTrendFact={precipitationTrendFact}
        />
      </NCCard2SectionLayout>
      <Divider />
      <NCCardComplianceFooter logos={COMPLIANCE_LOGOS_WATER} />
    </NCCard>
  );
};
